import { useCallback, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { useForm } from '@kanda-libs/ks-component-ts';
import { type StringIndexedObject } from '@kanda-libs/ks-design-library';
import { Credit, services, useSubmit } from '@kanda-libs/ks-frontend-services';

import { useCurrentJob, useCurrentJobLead, useGetCache } from 'hooks';
import { serviceConvert } from 'utils';

import {
  checkHasCredit,
  checkHasSecondLine,
  getFinanceOption,
  getHasApplicableLoans,
} from './helpers';
import useNewRate from './useNewRate';
import useReapply from './useReapply';

interface QuoteForm {
  loanAmount: string;
  depositAmount: string;
  editLoanAmount: number;
}

export interface QuoteFormHook {
  form: UseFormReturn<QuoteForm>;
  onSubmit: (
    data: StringIndexedObject,
    event?: React.BaseSyntheticEvent,
  ) => void;
  isLoading: boolean;
  isSubmitting: boolean;
  isSecondLine: boolean;
  hasApplicableLoans: boolean;
  selectNewRate: boolean;
}

function useQuoteForm(): QuoteFormHook {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Get job
  const { job } = useCurrentJob();
  // Get lead
  const { lead } = useCurrentJobLead();
  // Get cache, assign cache key from lead
  const { cache } = useGetCache();
  const cacheKey = lead?.id || '';
  // Extract credit from cache if available
  const cachedCredit: Credit | undefined = useMemo(() => {
    if (!cache || !cacheKey) return undefined;
    const stored = cache?.[cacheKey];
    if (!stored) return undefined;
    const parsed = JSON.parse(stored);
    return parsed?.credit;
  }, [cache, cacheKey]);

  const hasPreviousCredit = useMemo(
    () => checkHasCredit(job, lead),
    [job, lead],
  );

  const { submit: putCache } = useSubmit(
    serviceConvert(services.infoCache.infoPutCache),
  );

  const deleteCache = useCallback(() => {
    if (!cache) return;
    putCache({
      body: {
        ...cache,
        [cacheKey]: '',
      },
    });
  }, [putCache, cache, cacheKey]);

  const { submit: submitNewRate } = useNewRate();
  const { submit: submitReapply } = useReapply();

  const hasApplicableLoans = getHasApplicableLoans(job, lead);

  const jobTotal = job?.total?.amount_vat_inclusive?.amount || 0;

  const isSecondLine = checkHasSecondLine(job);

  const form = useForm<QuoteForm>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      loanAmount: String(jobTotal),
      depositAmount: '0',
      editLoanAmount: jobTotal,
    },
  });

  const selectNewRate = useMemo(
    () => Boolean(hasPreviousCredit && !isSecondLine && cachedCredit),
    [hasPreviousCredit, isSecondLine, cachedCredit],
  );

  const onSubmit = useCallback(
    (formValues: StringIndexedObject) => {
      if (!job) return;
      const rateToUse = getFinanceOption(
        job,
        lead,
        formValues?.duration,
        Math.ceil(parseFloat(formValues?.interest) * 100),
        formValues?.provider,
      );
      const deposit = parseInt(formValues.depositAmount || '', 10);
      const id = job?.id;
      if (!rateToUse || !id) return;
      setIsSubmitting(true);
      if (selectNewRate && cachedCredit) {
        const onSuccess = () => {
          deleteCache();
          setIsSubmitting(false);
        };
        submitNewRate(id, cachedCredit, deposit, rateToUse, onSuccess);
        return;
      }
      submitReapply(id, deposit, rateToUse, () => setIsSubmitting(false));
    },
    [
      submitNewRate,
      submitReapply,
      job,
      lead,
      cachedCredit,
      deleteCache,
      selectNewRate,
    ],
  );

  return {
    form,
    onSubmit,
    isSubmitting: Boolean(isSubmitting),
    isLoading: !job,
    isSecondLine,
    hasApplicableLoans,
    selectNewRate,
  };
}

export default useQuoteForm;
